import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ShellConfigService } from './shell-config.service';
import { TokenStorageService } from './token-storage.service';
import { AuthService } from './auth.service';

/** User payload returned by POST /Auth/dev-login (mirror of the backend AuthUserDto). */
export interface DevLoginUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  companyId: string;
  preferredCulture?: string;
}

/** Result of a dev-login attempt. `success:false` carries a human-readable `error`. */
export interface DevLoginResult {
  success: boolean;
  user?: DevLoginUser;
  error?: string;
}

interface DevAuthConfigResponse {
  enabled: boolean;
  useDefaultSecret: boolean;
}
interface DevLoginResponse {
  success: boolean;
  accessToken?: string;
  expiresIn?: number;
  user?: DevLoginUser;
  error?: string;
  error_description?: string;
}

/**
 * Frame-independent DEV credential login (ADO #1608). Talks to the backend mock-auth
 * endpoints (gated server-side: MockAuth:Enabled + Local/Dev/Test). On success it stores
 * the bearer in {@link TokenStorageService} and loads the canonical user via
 * {@link AuthService.loadUser} (the dev-login response also sets this host's refresh cookie),
 * so the existing shell session machinery (currentUser$, refresh, guards) just works.
 *
 * This is the dev counterpart to the B2C login-initiation gateway; it does NOT replace the
 * cookie→bearer startup probe in AuthService.
 */
@Injectable({ providedIn: 'root' })
export class DevAuthProvider {
  private http = inject(HttpClient);
  private config = inject(ShellConfigService);
  private tokenStorage = inject(TokenStorageService);
  private auth = inject(AuthService);

  /** Cached dev-config availability (null = not probed yet). */
  private availability: boolean | null = null;

  private get apiUrl(): string {
    return this.config.apiUrl;
  }
  private skipHeaders(): HttpHeaders {
    return new HttpHeaders().set('X-Skip-Interceptor', 'true');
  }

  /** Whether the backend has dev-auth enabled (GET /Auth/dev-config). Cached; never throws. */
  isAvailable(): Observable<boolean> {
    if (this.availability !== null) {
      return of(this.availability);
    }
    return this.http
      .get<DevAuthConfigResponse>(`${this.apiUrl}/Auth/dev-config`, {
        headers: this.skipHeaders(),
      })
      .pipe(
        map((c) => !!c?.enabled),
        tap((enabled) => (this.availability = enabled)),
        // Do NOT cache a transient probe error (backend still booting / network blip) —
        // leave `availability` null so a later call re-probes. Only a real answer is cached.
        catchError(() => of(false))
      );
  }

  /**
   * POST /Auth/dev-login. On success: store the bearer, clear the roles cache, and load the
   * canonical user (GET /Auth/me) so currentUser$ reflects the session. Returns a
   * {@link DevLoginResult} — never throws for expected auth errors (wrong secret etc.).
   */
  loginWithCredentials(email: string, secret: string): Observable<DevLoginResult> {
    if (!secret) {
      return of({ success: false, error: 'Dev authentication requires a secret' });
    }
    return this.http
      .post<DevLoginResponse>(
        `${this.apiUrl}/Auth/dev-login`,
        { email, secret },
        { headers: this.skipHeaders(), withCredentials: true }
      )
      .pipe(
        switchMap((res) => {
          if (!res?.success || !res.accessToken) {
            return of({
              success: false,
              error: res?.error_description || res?.error || 'Dev authentication failed',
            } as DevLoginResult);
          }
          this.tokenStorage.setToken(res.accessToken, res.expiresIn ?? 3600);
          this.auth.clearRolesCache();
          return this.auth.loadUser().pipe(
            map(() => ({ success: true, user: res.user } as DevLoginResult)),
            catchError(() => of({ success: true, user: res.user } as DevLoginResult))
          );
        }),
        catchError((err) =>
          of({
            success: false,
            error: err?.error?.error_description || err?.error?.error || 'Dev authentication failed',
          } as DevLoginResult)
        )
      );
  }
}
